import { SegmentId } from '../segment/segment-id';

/**
 * Comparison shortcut preferences.
 *
 * `recentComparisons` holds the ids of the comparisons the member opened,
 * newest first, distinct and capped; `pinnedComparisons` holds the ids the
 * member pinned, distinct, in the order they were pinned (the card sorts them
 * by alias). Both are absent until the member has one, so a missing section
 * means "nothing recorded" rather than an empty list to render. A row written
 * before pins became a list carries the one id under `pinnedComparison`; the
 * parser folds it into the list.
 */

/** The recent list keeps this many ids; the oldest fall off the end. */
export const MAX_RECENT_COMPARISONS = 5;

/** Ids newest first: whatever does not brand, and repeats, are dropped, the rest capped. */
export function mergeRecentComparisons(raw: unknown): readonly SegmentId[] {
  if (!Array.isArray(raw)) return [];
  const ids = raw
    .map((v) => (typeof v === 'string' ? storedSegmentId(v) : null))
    .filter((id): id is SegmentId => id !== null);
  return [...new Set(ids)].slice(0, MAX_RECENT_COMPARISONS);
}

/**
 * The pinned ids, distinct: `raw` is the stored list, `legacy` the single id an older row
 * holds. Anything that does not brand as a SegmentId is dropped. The legacy id counts only
 * when no list is stored, so a member who unpinned it after the change does not get it back.
 */
export function mergePinnedComparisons(raw: unknown, legacy: unknown): readonly SegmentId[] {
  if (Array.isArray(raw)) {
    const ids = raw
      .map((v) => (typeof v === 'string' ? storedSegmentId(v) : null))
      .filter((id): id is SegmentId => id !== null);
    return [...new Set(ids)];
  }
  const single = typeof legacy === 'string' ? storedSegmentId(legacy) : null;
  return single ? [single] : [];
}

/** The list after a pin toggle on `id`: off the list when on it, at its end when not. */
export function withPinToggled(list: readonly SegmentId[], id: SegmentId): readonly SegmentId[] {
  return list.includes(id) ? list.filter((known) => known !== id) : [...list, id];
}

/**
 * The recent list after a visit to `id`: the visit first, an earlier visit to it dropped, the
 * oldest falling off past the cap.
 */
export function withVisit(list: readonly SegmentId[], id: SegmentId): readonly SegmentId[] {
  return [id, ...list.filter((known) => known !== id)].slice(0, MAX_RECENT_COMPARISONS);
}

// One stored id the constructor refuses must not take the whole read down.
function storedSegmentId(raw: string): SegmentId | null {
  try {
    return SegmentId(raw);
  } catch (e) {
    if (!(e instanceof Error)) throw e;
    return null;
  }
}
